export interface AnchoredListing {
  id: string;
  title?: string;
  url?: string;
  photos?: string[];
  price_thb?: string | number;
  usd_price?: string | number;
  price_quote?: string | number;
  quote_currency?: string;
  district?: string;
  area_sqm?: string | number;
  property_type?: string;
}

export type MessageSegment<T extends AnchoredListing> =
  { kind: 'text'; text: string } | { kind: 'listing'; listing: T };

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Strip common Markdown decorations so plain chat stays readable if the model slips. */
export function plainChatText(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|\s)\*([^*\n]+)\*(?=\s|$)/g, '$1$2')
    .replace(/(^|\s)_([^_\n]+)_(?=\s|$)/g, '$1$2')
    .replace(/^#{1,6}\s+/gm, '');
}

function stripListingAnchor(line: string, id: string): string {
  return plainChatText(
    line
      .replace(new RegExp(`id\\s*[=:]?\\s*${escapeRegExp(id)}`, 'i'), '')
      .replace(new RegExp(escapeRegExp(id), 'g'), '')
      .replace(/[()[\]]/g, '')
      .replace(/^[\s\u2014\u2013\-–—:,.]+/, '')
      .replace(/[\s\u2014\u2013\-–—:,.]+$/, '')
      .trim(),
  );
}

/**
 * The agent anchors every listing with `id=<id>`; each match is replaced by a
 * photo card (never shown as a raw id). Unmatched anchors stay visible so
 * missing attachments are obvious in playground.
 */
export function splitMessageByListings<T extends AnchoredListing>(
  content: string,
  listings: T[] | undefined,
): MessageSegment<T>[] {
  const onlyListings = (listings ?? []).filter((listing) => Boolean(listing?.id));
  if (onlyListings.length === 0) {
    const text = plainChatText(content);
    return text.trim() ? [{ kind: 'text', text }] : [];
  }

  const pending = new Map(onlyListings.map((listing) => [listing.id, listing]));
  const segments: MessageSegment<T>[] = [];
  let buffer: string[] = [];

  const flush = () => {
    const text = plainChatText(buffer.join('\n')).replace(/^\n+|\n+$/g, '');
    if (text.trim()) segments.push({ kind: 'text', text });
    buffer = [];
  };

  for (const line of content.split('\n')) {
    const matched = [...pending.values()].find((listing) => line.includes(listing.id));
    if (!matched) {
      buffer.push(line);
      continue;
    }
    pending.delete(matched.id);
    const residual = stripListingAnchor(line, matched.id);
    if (residual.trim()) buffer.push(residual);
    flush();
    segments.push({ kind: 'listing', listing: matched });
  }
  flush();

  for (const listing of pending.values()) {
    segments.push({ kind: 'listing', listing });
  }
  return segments;
}
